/**
 * Button Component - CP Admin 2077
 *
 * A versatile button component with Cyberpunk 2077 aesthetic featuring:
 * - Sharp corners (border-radius: 0)
 * - Orange gradient backgrounds
 * - Glow effects on hover
 * - Rajdhani font with UPPERCASE text
 * - Full keyboard navigation support
 * - Loading states with spinner
 * - Icon support (left/right)
 *
 * @example
 * ```tsx
 * <Button variant="primary" onClick={handleClick}>
 *   Submit
 * </Button>
 *
 * <Button variant="secondary" size="sm" icon={<Icon />} loading>
 *   Processing...
 * </Button>
 * ```
 */

import React, { type ButtonHTMLAttributes } from 'react'
import {
  COLORS,
  BORDER_RADIUS,
  TYPOGRAPHY,
  SHADOW_PRESETS,
  ANIMATIONS,
  GRADIENTS,
  SPACING,
} from '@/styles/theme'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Button visual variant
   * @default 'primary'
   */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'success' | 'warning' | 'danger'

  /**
   * Button size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg'

  /**
   * Loading state - shows spinner and disables interaction
   * @default false
   */
  loading?: boolean

  /**
   * Icon to display (left side by default)
   */
  icon?: React.ReactNode

  /**
   * Icon position
   * @default 'left'
   */
  iconPosition?: 'left' | 'right'

  /**
   * Make button full width
   * @default false
   */
  fullWidth?: boolean
}

/**
 * Loading Spinner Component
 */
const Spinner: React.FC<{ size: 'sm' | 'md' | 'lg' }> = ({ size }) => {
  const sizes = {
    sm: '12px',
    md: '16px',
    lg: '20px',
  }

  return (
    <svg
      width={sizes[size]}
      height={sizes[size]}
      viewBox="0 0 24 24"
      fill="none"
      style={{
        animation: 'spin 1s linear infinite',
      }}
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="31.415 31.415"
        opacity="0.25"
      />
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="15.7075 47.1225"
        opacity="0.75"
      />
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </svg>
  )
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      iconPosition = 'left',
      fullWidth = false,
      disabled,
      children,
      className = '',
      style = {},
      ...props
    },
    ref
  ) => {
    // Variant styles
    const variantStyles: Record<string, React.CSSProperties> = {
      primary: {
        background: GRADIENTS.buttonPrimary,
        border: `2px solid ${COLORS.primary.orangeLight}`,
        color: COLORS.text.primary,
        boxShadow: SHADOW_PRESETS.buttonDefault,
      },
      secondary: {
        background: 'transparent',
        border: `2px solid ${COLORS.primary.orange}`,
        color: COLORS.primary.orange,
        boxShadow: `0 0 10px rgba(255, 107, 53, 0.3)`,
      },
      tertiary: {
        background: 'transparent',
        border: `2px solid ${COLORS.text.secondary}`,
        color: COLORS.text.primary,
        boxShadow: 'none',
      },
      success: {
        background: `linear-gradient(90deg, ${COLORS.status.success}, #5FBC6F)`,
        border: `2px solid ${COLORS.status.success}`,
        color: COLORS.text.primary,
        boxShadow: `0 0 15px rgba(107, 207, 127, 0.5)`,
      },
      warning: {
        background: `linear-gradient(90deg, ${COLORS.status.warning}, #FFB733)`,
        border: `2px solid ${COLORS.status.warning}`,
        color: COLORS.text.primary,
        boxShadow: `0 0 15px rgba(255, 165, 0, 0.5)`,
      },
      danger: {
        background: `linear-gradient(90deg, ${COLORS.status.error}, #FF3333)`,
        border: `2px solid ${COLORS.status.error}`,
        color: COLORS.text.primary,
        boxShadow: `0 0 15px rgba(255, 0, 0, 0.5)`,
      },
    }

    // Size styles
    const sizeStyles: Record<string, React.CSSProperties> = {
      sm: {
        padding: `${SPACING.sm} ${SPACING.lg}`,
        fontSize: TYPOGRAPHY.fontSize.sm,
        minHeight: '32px',
      },
      md: {
        padding: `${SPACING.md} ${SPACING.xl}`,
        fontSize: TYPOGRAPHY.fontSize.md,
        minHeight: '40px',
      },
      lg: {
        padding: `${SPACING.lg} ${SPACING.xxl}`,
        fontSize: TYPOGRAPHY.fontSize.lg,
        minHeight: '48px',
      },
    }

    // Base styles
    const baseStyles: React.CSSProperties = {
      // Layout
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: SPACING.sm,
      width: fullWidth ? '100%' : 'auto',

      // Typography
      fontFamily: TYPOGRAPHY.fontFamily.primary,
      fontWeight: TYPOGRAPHY.fontWeight.bold,
      textTransform: 'uppercase',
      letterSpacing: TYPOGRAPHY.letterSpacing.widest,

      // Appearance
      borderRadius: BORDER_RADIUS.none, // Sharp corners for CP 2077
      cursor: disabled || loading ? 'not-allowed' : 'pointer',
      transition: ANIMATIONS.transition.normal,
      position: 'relative',
      overflow: 'hidden',

      // Disabled/Loading state
      opacity: disabled || loading ? 0.6 : 1,
      pointerEvents: disabled || loading ? 'none' : 'auto',
    }

    // Hover styles (applied via pseudo-class)
    const hoverStyles: React.CSSProperties = {
      transform: ANIMATIONS.hover.scale,
      boxShadow: variant === 'primary' ? SHADOW_PRESETS.buttonHover : undefined,
    }

    // Active styles
    const activeStyles: React.CSSProperties = {
      transform: ANIMATIONS.hover.press,
    }

    // Combined styles
    const buttonStyles: React.CSSProperties = {
      ...baseStyles,
      ...variantStyles[variant],
      ...sizeStyles[size],
      ...style,
    }

    // Icon rendering
    const renderIcon = () => {
      if (loading) {
        return <Spinner size={size} />
      }
      return icon
    }

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        aria-disabled={disabled || loading}
        aria-busy={loading}
        aria-label={props['aria-label']}
        className={`cp2077-button ${className}`.trim()}
        style={buttonStyles}
        {...props}
      >
        {/* Scanline effect overlay */}
        <span
          style={{
            position: 'absolute',
            inset: 0,
            background: GRADIENTS.scanlines,
            pointerEvents: 'none',
            opacity: 0.3,
          }}
        />

        {/* Content */}
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: SPACING.sm,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {iconPosition === 'left' && renderIcon()}
          {children}
          {iconPosition === 'right' && renderIcon()}
        </span>

        {/* Inline styles for hover/active states */}
        <style>
          {`
            .cp2077-button:hover:not(:disabled) {
              transform: ${hoverStyles.transform};
              box-shadow: ${hoverStyles.boxShadow || buttonStyles.boxShadow};
            }
            .cp2077-button:active:not(:disabled) {
              transform: ${activeStyles.transform};
            }
            .cp2077-button:focus-visible {
              outline: 3px solid ${COLORS.border.focus};
              outline-offset: 2px;
            }
          `}
        </style>
      </button>
    )
  }
)

Button.displayName = 'Button'
